import { useState, FormEvent } from "react";
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import { BookOpenIcon } from "@heroicons/react/24/outline";
import TextInputTitle from "../ui/common/TextInputTitle";
import TextInputDescription from "../ui/common/TextInputDescription";
import { Book } from "../../utils/types";
import { ChangeEvent } from "react";
import Alert from "../ui/Alert";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import { defaultBook } from "../../utils/templates";
import { CreateBookProps } from "../../utils/types";

/** Renders form that holds book information
 *
 *
 * [CreateBookRequests, CreateBookCopyRecipeProps] -> CreateBook -> [TextInputTitle, TextInputDescription]
 */
function CreateBook({ isOpen, onCloseDialog, createBook }: CreateBookProps) {
  const [bookData, setBookData] = useState<Book>(defaultBook);
  const [alert, setAlert] = useState("");

  /** Handles changes to book data form */
  function handleChange(event: ChangeEvent<HTMLTextAreaElement>) {
    const { name, value } = event.target;
    setBookData((bookData) => {
      const updatedBook = { ...bookData };
      updatedBook[name] = value;
      return updatedBook;
    });
  }

  /** Handle submitting action */
  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const newBook = await createBook(bookData);
    if (newBook.is_default_replaced)
      setAlert(
        `Your new recipe book, "${newBook.title}" will be set as the default`,
      );
    if (!newBook.is_default_replaced) handleClose();
  }

  function handleCloseOnAlert() {
    setAlert("");
    onCloseDialog();
    setBookData(defaultBook);
  }

  function handleClose() {
    onCloseDialog();
    setTimeout(() => {
      // prevents flash of default recipe
      setBookData(defaultBook);
    }, 500);
  }

  return (
    <Dialog open={isOpen} onClose={handleClose} className="relative z-10">
      <DialogBackdrop
        transition
        className="CreateBook-container fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
      />

      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <DialogPanel
            id="CreateBook-DialogPanel"
            transition
            className="relative bg-primary transform overflow-hidden rounded-lg px-4 pb-4 pt-5 text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-lg sm:p-6 data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
          >
            {alert ? (
              <div className="flex flex-row">
                <Alert alert={alert} degree={"yellow"} />
                <button
                  onClick={handleCloseOnAlert}
                  type="button"
                  className="ms-auto -mx-1.5 -my-1.5 bg-blue-50 text-blue-500 rounded-lg focus:ring-2 focus:ring-blue-400 p-1.5 hover:bg-blue-200 inline-flex items-center justify-center h-8 w-8 dark:bg-gray-800 dark:text-blue-400 dark:hover:bg-gray-700"
                  aria-label="Close"
                >
                  <FontAwesomeIcon icon={faXmark} />
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                <div>
                  <div className="mx-auto bg-background-color flex h-12 w-12 items-center justify-center rounded-full">
                    <BookOpenIcon
                      aria-hidden="true"
                      className="h-6 w-6 text-secondary"
                    />
                  </div>
                  <div className="mt-3 text-center sm:mt-5">
                    <DialogTitle as="h3" className="text-base leading-6">
                      <TextInputTitle
                        isRequired={true}
                        handleChange={handleChange}
                        title={bookData.title}
                      />
                    </DialogTitle>
                    <div className="CreateBook-description mt-6">
                      <TextInputDescription
                        onChange={handleChange}
                        description={bookData.description}
                      />
                    </div>
                  </div>
                </div>
                <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                  <button
                    id="submit-button"
                    type="submit"
                    className="inline-flex w-full justify-center rounded-md bg-button-submit px-3 py-2 text-sm font-semibold shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-button-disabled sm:col-start-2"
                  >
                    Submit
                  </button>
                  <button
                    id="cancel-button"
                    type="button"
                    data-autofocus
                    onClick={handleClose}
                    className="mt-3 inline-flex w-full justify-center rounded-md bg-button-cancel px-3 py-2 text-sm font-semibold shadow-sm ring-1 ring-inset ring-button-submit sm:col-start-1 sm:mt-0"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
}
export default CreateBook;
